/**
 * useUIStore.ts - UI State Store
 *
 * Issue #1282: Zustand Store分割 Phase 2
 * UI関連の状態管理を担当するStore
 *
 * 担当範囲:
 * - UI Layout (sidebar, page, tabs)
 * - Feature States (onboarding, emergency, batch, inventory)
 * - AI Chat UI
 * - Notifications
 *
 * @module store/useUIStore
 */
import { create } from 'zustand';
import logger from '../utils/logger';

// ============================================================
// Constants
// ============================================================

const AI_CHAT_STORAGE_KEY = 'ripia-ai-chat-history';

// ============================================================
// Type Definitions
// ============================================================

export interface OnboardingState {
  isFirstTime: boolean;
  currentStep: number;
  completedTutorials: string[];
  beginnerMode: boolean;
  progressPercentage: number;
}

export interface EmergencyState {
  isActive: boolean;
  incidentType: 'medical' | 'allergy' | 'accident' | null;
  startTime: Date | null;
  involvedCustomer: string | null;
}

export interface BatchState {
  isMultiSelectMode: boolean;
  selectedItems: string[];
  currentOperation: string | null;
  progress: number;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface AIState {
  messages: ChatMessage[];
  isOpen: boolean;
  isMinimized: boolean;
}

export interface InventoryAlert {
  id: string;
  itemName: string;
  currentLevel: number;
  threshold: number;
  severity: 'critical' | 'warning' | 'info';
  category: 'chemical' | 'consumable' | 'tool';
}

export interface CustomInventoryItem {
  id: string;
  name: string;
  category: string;
  threshold: number;
  unit: string;
}

export interface InventoryState {
  alerts: InventoryAlert[];
  customItems: CustomInventoryItem[];
  lastChecked: Date;
}

// Notification Types
export type NotificationPriority = 'critical' | 'high' | 'medium' | 'low';
export type NotificationCategory =
  | 'reservation'
  | 'customer'
  | 'system'
  | 'staff';
export type PeriodFilter = 'today' | 'week' | 'month' | 'all';
export type CategoryFilter =
  | 'all'
  | 'reservation'
  | 'customer'
  | 'system'
  | 'staff';

export interface StoreNotification {
  id: string;
  icon: React.ReactElement;
  primary: string;
  secondary: string;
  timestamp: number;
  priority: NotificationPriority;
  isNew: boolean;
  isRead: boolean;
  onClick: () => void;
  actionLabel?: string;
  category?: NotificationCategory;
}

export interface NotificationState {
  notifications: StoreNotification[];
  unreadCount: number;
  isModalOpen: boolean;
  periodFilter: PeriodFilter;
  categoryFilter: CategoryFilter;
}

// ============================================================
// Store State Interface
// ============================================================

interface UIState {
  // UI Layout State
  isSidebarOpen: boolean;
  currentPage: string;
  selectedCustomerId: string | null;
  salesActiveTab: number;

  // Feature States
  onboarding: OnboardingState;
  emergency: EmergencyState;
  batch: BatchState;
  inventory: InventoryState;
  ai: AIState;
  notification: NotificationState;

  // UI Layout Actions
  setSidebarOpen: (isOpen: boolean) => void;
  setCurrentPage: (page: string) => void;
  setSelectedCustomerId: (customerId: string | null) => void;
  setSalesActiveTab: (tab: number) => void;

  // Onboarding Actions
  setBeginnerMode: (enabled: boolean) => void;
  updateOnboardingProgress: (step: number, percentage: number) => void;
  completeTutorial: (tutorialId: string) => void;

  // Emergency Actions
  activateEmergency: (
    type: EmergencyState['incidentType'],
    customerId?: string
  ) => void;
  deactivateEmergency: () => void;

  // Batch Actions
  setMultiSelectMode: (enabled: boolean) => void;
  toggleBatchSelection: (itemId: string) => void;
  clearBatchSelection: () => void;
  setBatchOperation: (operation: string | null, progress?: number) => void;

  // Inventory Actions
  updateInventoryAlerts: (alerts: InventoryAlert[]) => void;
  addCustomInventoryItem: (item: Omit<CustomInventoryItem, 'id'>) => void;

  // AI Chat Actions
  addAIMessage: (message: ChatMessage) => void;
  clearAIMessages: () => void;
  toggleAIChat: () => void;
  setAIMinimized: (minimized: boolean) => void;
  loadAIFromLocalStorage: () => void;
  saveAIToLocalStorage: () => void;

  // Notification Actions
  addNotification: (
    notification: Omit<StoreNotification, 'id' | 'timestamp' | 'isRead'>
  ) => void;
  markNotificationAsRead: (notificationId: string) => void;
  markAllNotificationsAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  toggleNotificationModal: () => void;
  setNotificationModalOpen: (isOpen: boolean) => void;
  getUnreadNotifications: () => StoreNotification[];
  initializeNotifications: () => void;
  setPeriodFilter: (filter: PeriodFilter) => void;
  setCategoryFilter: (filter: CategoryFilter) => void;

  // Reset Action (for logout)
  resetUIState: () => void;
}

// ============================================================
// Initial State
// ============================================================

const initialOnboardingState: OnboardingState = {
  isFirstTime: true,
  currentStep: 0,
  completedTutorials: [],
  beginnerMode: false,
  progressPercentage: 0,
};

const initialEmergencyState: EmergencyState = {
  isActive: false,
  incidentType: null,
  startTime: null,
  involvedCustomer: null,
};

const initialBatchState: BatchState = {
  isMultiSelectMode: false,
  selectedItems: [],
  currentOperation: null,
  progress: 0,
};

const initialInventoryState: InventoryState = {
  alerts: [
    {
      id: '1',
      itemName: 'カラー剤（ブラウン）',
      currentLevel: 2,
      threshold: 5,
      severity: 'critical',
      category: 'chemical',
    },
    {
      id: '2',
      itemName: 'シャンプー',
      currentLevel: 8,
      threshold: 10,
      severity: 'warning',
      category: 'consumable',
    },
    {
      id: '3',
      itemName: 'タオル',
      currentLevel: 15,
      threshold: 20,
      severity: 'info',
      category: 'consumable',
    },
  ],
  customItems: [],
  lastChecked: new Date(),
};

const initialAIState: AIState = {
  messages: [],
  isOpen: false,
  isMinimized: false,
};

const initialNotificationState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  isModalOpen: false,
  periodFilter: 'all',
  categoryFilter: 'all',
};

// ============================================================
// Store Implementation
// ============================================================

export const useUIStore = create<UIState>()((set, get) => ({
  // Initial State
  isSidebarOpen: true,
  currentPage: 'dashboard',
  selectedCustomerId: null,
  salesActiveTab: 0,
  onboarding: initialOnboardingState,
  emergency: initialEmergencyState,
  batch: initialBatchState,
  inventory: initialInventoryState,
  ai: initialAIState,
  notification: initialNotificationState,

  // UI Layout Actions
  setSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setSelectedCustomerId: (customerId) =>
    set({ selectedCustomerId: customerId }),
  setSalesActiveTab: (tab) => set({ salesActiveTab: tab }),

  // Onboarding Actions
  setBeginnerMode: (enabled) =>
    set((state) => ({
      onboarding: { ...state.onboarding, beginnerMode: enabled },
    })),

  updateOnboardingProgress: (step, percentage) =>
    set((state) => ({
      onboarding: {
        ...state.onboarding,
        currentStep: step,
        progressPercentage: percentage,
      },
    })),

  completeTutorial: (tutorialId) =>
    set((state) => ({
      onboarding: {
        ...state.onboarding,
        completedTutorials: [
          ...state.onboarding.completedTutorials,
          tutorialId,
        ],
      },
    })),

  // Emergency Actions
  activateEmergency: (type, customerId) =>
    set({
      emergency: {
        isActive: true,
        incidentType: type,
        startTime: new Date(),
        involvedCustomer: customerId || null,
      },
    }),

  deactivateEmergency: () =>
    set({
      emergency: {
        isActive: false,
        incidentType: null,
        startTime: null,
        involvedCustomer: null,
      },
    }),

  // Batch Actions
  setMultiSelectMode: (enabled) =>
    set((state) => ({
      batch: {
        ...state.batch,
        isMultiSelectMode: enabled,
        selectedItems: enabled ? state.batch.selectedItems : [],
      },
    })),

  toggleBatchSelection: (itemId) =>
    set((state) => {
      const isSelected = state.batch.selectedItems.includes(itemId);
      return {
        batch: {
          ...state.batch,
          selectedItems: isSelected
            ? state.batch.selectedItems.filter((id) => id !== itemId)
            : [...state.batch.selectedItems, itemId],
        },
      };
    }),

  clearBatchSelection: () =>
    set((state) => ({
      batch: {
        ...state.batch,
        selectedItems: [],
        currentOperation: null,
        progress: 0,
      },
    })),

  setBatchOperation: (operation, progress) =>
    set((state) => ({
      batch: {
        ...state.batch,
        currentOperation: operation,
        progress: progress || 0,
      },
    })),

  // Inventory Actions
  updateInventoryAlerts: (alerts) =>
    set((state) => ({
      inventory: { ...state.inventory, alerts, lastChecked: new Date() },
    })),

  addCustomInventoryItem: (item) =>
    set((state) => ({
      inventory: {
        ...state.inventory,
        customItems: [
          ...state.inventory.customItems,
          { ...item, id: Date.now().toString() },
        ],
      },
    })),

  // AI Chat Actions
  addAIMessage: (message) =>
    set((state) => {
      const newMessages = [...state.ai.messages, message];
      // Save to LocalStorage
      try {
        localStorage.setItem(
          AI_CHAT_STORAGE_KEY,
          JSON.stringify({
            messages: newMessages,
            lastUpdated: new Date().toISOString(),
          })
        );
      } catch (error) {
        logger.error('Failed to save AI chat to localStorage:', error);
      }
      return { ai: { ...state.ai, messages: newMessages } };
    }),

  clearAIMessages: () =>
    set((state) => {
      // Clear from LocalStorage
      try {
        localStorage.removeItem(AI_CHAT_STORAGE_KEY);
      } catch (error) {
        logger.error('Failed to clear AI chat from localStorage:', error);
      }
      return { ai: { ...state.ai, messages: [] } };
    }),

  toggleAIChat: () =>
    set((state) => ({
      ai: { ...state.ai, isOpen: !state.ai.isOpen, isMinimized: false },
    })),

  setAIMinimized: (minimized) =>
    set((state) => ({
      ai: { ...state.ai, isMinimized: minimized },
    })),

  loadAIFromLocalStorage: () => {
    try {
      const stored = localStorage.getItem(AI_CHAT_STORAGE_KEY);
      if (stored) {
        const { messages } = JSON.parse(stored);
        // Convert timestamp strings to Date objects
        const parsedMessages = messages.map(
          (msg: { timestamp: string | Date; [key: string]: unknown }) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          })
        );
        set((state) => ({ ai: { ...state.ai, messages: parsedMessages } }));
      }
    } catch (error) {
      logger.error('Failed to load AI chat from localStorage:', error);
    }
  },

  saveAIToLocalStorage: () => {
    const state = get();
    try {
      localStorage.setItem(
        AI_CHAT_STORAGE_KEY,
        JSON.stringify({
          messages: state.ai.messages,
          lastUpdated: new Date().toISOString(),
        })
      );
    } catch (error) {
      logger.error('Failed to save AI chat to localStorage:', error);
    }
  },

  // Notification Actions
  addNotification: (notification) =>
    set((state) => {
      const newNotification: StoreNotification = {
        ...notification,
        id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        isRead: false,
      };

      const updatedNotifications = [
        newNotification,
        ...state.notification.notifications,
      ];
      const unreadCount = updatedNotifications.filter((n) => !n.isRead).length;

      logger.log('[UIStore] Notification added:', newNotification.id);

      return {
        notification: {
          ...state.notification,
          notifications: updatedNotifications,
          unreadCount,
        },
      };
    }),

  markNotificationAsRead: (notificationId) =>
    set((state) => {
      const updatedNotifications = state.notification.notifications.map((n) =>
        n.id === notificationId ? { ...n, isRead: true, isNew: false } : n
      );
      const unreadCount = updatedNotifications.filter((n) => !n.isRead).length;

      logger.log(`[UIStore] Notification ${notificationId} marked as read`);

      return {
        notification: {
          ...state.notification,
          notifications: updatedNotifications,
          unreadCount,
        },
      };
    }),

  markAllNotificationsAsRead: () =>
    set((state) => {
      const updatedNotifications = state.notification.notifications.map(
        (n) => ({
          ...n,
          isRead: true,
          isNew: false,
        })
      );

      logger.log('[UIStore] All notifications marked as read');

      return {
        notification: {
          ...state.notification,
          notifications: updatedNotifications,
          unreadCount: 0,
        },
      };
    }),

  deleteNotification: (notificationId) =>
    set((state) => {
      const updatedNotifications = state.notification.notifications.filter(
        (n) => n.id !== notificationId
      );
      const unreadCount = updatedNotifications.filter((n) => !n.isRead).length;

      logger.log(`[UIStore] Notification ${notificationId} deleted`);

      return {
        notification: {
          ...state.notification,
          notifications: updatedNotifications,
          unreadCount,
        },
      };
    }),

  clearAllNotifications: () =>
    set((state) => {
      logger.log('[UIStore] All notifications cleared');

      return {
        notification: {
          ...state.notification,
          notifications: [],
          unreadCount: 0,
        },
      };
    }),

  toggleNotificationModal: () =>
    set((state) => ({
      notification: {
        ...state.notification,
        isModalOpen: !state.notification.isModalOpen,
      },
    })),

  setNotificationModalOpen: (isOpen) =>
    set((state) => ({
      notification: {
        ...state.notification,
        isModalOpen: isOpen,
      },
    })),

  getUnreadNotifications: () => {
    const state = get();
    return state.notification.notifications.filter((n) => !n.isRead);
  },

  initializeNotifications: () => {
    logger.log('[UIStore] Notifications initialized');
  },

  setPeriodFilter: (filter) =>
    set((state) => {
      logger.log(`[UIStore] Period filter changed to: ${filter}`);
      return {
        notification: {
          ...state.notification,
          periodFilter: filter,
        },
      };
    }),

  setCategoryFilter: (filter) =>
    set((state) => {
      logger.log(`[UIStore] Category filter changed to: ${filter}`);
      return {
        notification: {
          ...state.notification,
          categoryFilter: filter,
        },
      };
    }),

  // Reset Action (for logout)
  resetUIState: () =>
    set({
      onboarding: initialOnboardingState,
      emergency: initialEmergencyState,
      batch: initialBatchState,
      inventory: {
        ...initialInventoryState,
        alerts: [],
        lastChecked: new Date(),
      },
      ai: initialAIState,
      notification: initialNotificationState,
    }),
}));

// ============================================================
// Selector Hooks (for optimized re-renders)
// ============================================================

export const useIsSidebarOpen = () =>
  useUIStore((state) => state.isSidebarOpen);
export const useCurrentPage = () => useUIStore((state) => state.currentPage);
export const useSelectedCustomerId = () =>
  useUIStore((state) => state.selectedCustomerId);
export const useSalesActiveTab = () =>
  useUIStore((state) => state.salesActiveTab);

export const useOnboarding = () => useUIStore((state) => state.onboarding);
export const useEmergency = () => useUIStore((state) => state.emergency);
export const useBatch = () => useUIStore((state) => state.batch);
export const useInventory = () => useUIStore((state) => state.inventory);
export const useAI = () => useUIStore((state) => state.ai);
export const useNotification = () => useUIStore((state) => state.notification);

export default useUIStore;
